import React from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';

import MediaQueryContainer from '../../containers/media-query-container/media-query-container';
import QueryParamManager from '../../containers/query-param-manager/query-param-manager';
import AuthenticationWrapped from '../../containers/authentication/authentication';

import {
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
  stringInputSpec,
  stringTextAreaSpec,
  boolCheckboxSpec,
  repeatable,
} from '../../lib/editing-configs';

const Page = ({
  className,
  title,
  children,
  history,
  queryParams,
  watchQueryParams,
  requiresAuth,
  authRoleRequired,
  loginModalImgOverride,
  loginModalCopyOverride,
  loginPageCopyOverride,
}) => (
  <div className={className}>
    <DocumentTitle title={title} />
    <MediaQueryContainer />
    <QueryParamManager
      watchQueryParams={watchQueryParams}
      queryParams={queryParams}
    />
    <AuthenticationWrapped
      requiresAuth={requiresAuth}
      authRoleRequired={authRoleRequired}
      loginModalImgOverride={loginModalImgOverride}
      loginModalCopyOverride={loginModalCopyOverride}
      loginPageCopyOverride={loginPageCopyOverride}
      onSuccess={history.location.pathname}
    >
      {children}
    </AuthenticationWrapped>
  </div>
);

Page.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  history: PropTypes.object,
  queryParams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  watchQueryParams: PropTypes.bool,
  includeInactiveOrNonMonetizingProducts: PropTypes.bool,
  requiresAuth: PropTypes.bool,
  authRoleRequired: PropTypes.string,
  loginModalImgOverride: PropTypes.string,
  loginModalCopyOverride: PropTypes.string,
  loginPageCopyOverride: PropTypes.string,
};

Page.displayName = 'Page';

Page.defaultProps = {
  className: null,
  children: null,
  history: null,
  title: '',
  requiresAuth: false,
  authRoleRequired: 'nerdwallet/logged_in',
};

Page.getEditingConfig = (componentDocument) => {
  const authRoleContent =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec(
            'authRoleRequired',
            'Role required for authentication',
            {
              help:
                'The role that is required for authentication. Please note: if you choose to require authentication for a page, this defaults to "nerdwallet/logged_in", meaning the page will be configured to require users to login to view the page\'s contents. If the page requires a more targeted role for authentication, please specify here. Also, please note that all pages that require authentication can be viewed by NerdWallet employees (eg: users who possess the "nerdwallet/employee" role).',
            }
          ),
          stringInputSpec(
            'loginPageCopyOverride',
            'Login splash page custom copy',
            {
              help:
                "If you'd like to output custom copy on the login splash page, please locate your custom output in this field.",
            }
          ),
          stringInputSpec(
            'loginModalImgOverride',
            'Custom login modal background image URL',
            {
              help:
                "If you'd like to use a custom image for the login modal background image, please indicate the image's URL in this field.",
            }
          ),
          stringInputSpec('loginModalCopyOverride', 'Custom login modal copy', {
            help:
              "If you'd like to output custom copy in the login modal, please locate your custom output in this field.",
          }),
        ];

  return [
    stringTextAreaSpec('title', 'Page title', {
      help: 'The title the user sees on their browser window',
    }),

    // Page meta info
    stringTextAreaSpec('meta.nlOpts.title', 'Page meta title', {
      isProp: false,
      help: 'Title seen by search engines',
    }),
    stringTextAreaSpec('meta.nlOpts.description', 'Page meta description', {
      isProp: false,
      help: 'Description seen by search engines',
    }),
    boolCheckboxSpec('meta.needsGlobalNav', 'Use standard NerdWallet nav', {
      isProp: false,
      help: 'Note: requires a save to take effect.',
    }),
    stringTextAreaSpec('meta.nlOpts.ogTags.title', 'Social media title', {
      isProp: false,
      help: "The title you'll see if you share this page on Facebook",
    }),
    stringTextAreaSpec(
      'meta.nlOpts.ogTags.description',
      'Social media description',
      {
        isProp: false,
        help: "The description you'll see if you share this page on Facebook",
      }
    ),
    stringInputSpec(
      'meta.nlOpts.ogTags.image',
      'Social media image URL, HTTP',
      {
        isProp: false,
        help:
          "HTTP URL for the image you'll see if you share this page on Facebook. Can be the same as the https one but with http at the beginning instead.",
      }
    ),
    stringInputSpec(
      'meta.nlOpts.ogTags.image:secure_url',
      'Social media image URL, HTTPS',
      {
        isProp: false,
        help:
          "HTTPS URL for the image you'll see if you share this page on Facebook",
      }
    ),
    stringTextAreaSpec('meta.nlOpts.ogTags.url', 'Social media url', {
      isProp: false,
      help: 'The link that will be used if this post is shared on Facebook',
    }),
    stringInputSpec('meta.nlOpts.ogTags.site_name', 'Social media site name', {
      isProp: false,
      help:
        'The site name that will be used if this post is shared on Facebook',
    }),
    stringInputSpec('meta.nlOpts.pageTaxonomy.0.value', 'Vertical', {
      isProp: false,
    }),
    stringInputSpec('meta.nlOpts.pageTaxonomy.1.value', 'Topic', {
      isProp: false,
    }),
    stringInputSpec('meta.nlOpts.pageTaxonomy.2.value', 'Page Type', {
      isProp: false,
    }),

    // Builder query params
    repeatable('queryParams', 'Current Query Params', 'Query param', () => [
      stringInputSpec('name', 'Query Param Name'),
      stringInputSpec('value', 'Query Param Value'),
    ]),
    boolCheckboxSpec('watchQueryParams', 'Filter components by query params?', {
      help:
        'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
    }),

    // Data dependencies
    // This field is read from the JSON and passed to the providers on creation in client/routes/container.jsx.
    boolCheckboxSpec(
      'includeInactiveOrNonMonetizingProducts',
      'Include inactive or non-monetizing products on page?',
      {
        help:
          'Products will appear on the page regardless of whether they are currently active on NerdWallet. Inactive products will come with messaging indicating them as such. You may need to save and refresh the page to see this change take effect.',
      }
    ),
    dataDependencyTypePicker,
    dataDependencyIdsAdder,
    stringInputSpec('dataDependency.data.slug', 'Slug', { isProp: false }),
    boolCheckboxSpec('requiresAuth', 'Page requires user authentication', {
      help:
        'In order to view page, user must be signed in and have the required role/scope',
    }),
    ...authRoleContent,
  ];
};

Page.isContainer = true;

export default compose(withRouter, connect())(Page);
